import React from 'react';
import { FiPlus, FiMinus, FiCheck } from 'react-icons/fi';
import { formatCurrency } from '../utils/formatters';
import { getItemOptions, TEMPERATURE_OPTIONS, SWEETNESS_OPTIONS, calculateItemPrice } from '../utils/itemOptions';

const ItemConfigPanel = ({
  item,
  options,
  setOptions,
  toppings,
  onAdd,
  onCancel,
  isMobile = false
}) => {
  if (!item) return null;

  const { showIceOptions, showSweetnessOptions, showToppingOptions } = getItemOptions(item);
  const availableToppings = toppings.filter(t => 
    item.availableToppings?.includes(t._id)
  );
  const totalPrice = calculateItemPrice(item, options);

  const toggleTopping = (topping) => {
    const exists = options.selectedToppings.find(t => t._id === topping._id);
    if (exists) {
      setOptions({
        ...options,
        selectedToppings: options.selectedToppings.filter(t => t._id !== topping._id)
      });
    } else {
      setOptions({
        ...options,
        selectedToppings: [...options.selectedToppings, topping]
      });
    }
  };

  return (
    <div className={`bg-white ${isMobile ? 'p-4' : 'p-4 border rounded-lg'}`}>
      {/* Item Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h4 className="font-bold text-lg">{item.name}</h4>
          <p className="text-green-600 font-medium">{formatCurrency(item.price)}</p>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Custom Options (Size, etc) */}
      {item.customOptions?.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
          <div className="flex flex-wrap gap-2">
            {item.customOptions.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => setOptions({ ...options, selectedOption: opt })}
                className={`px-3 py-2 rounded-lg border ${
                  options.selectedOption?.name === opt.name
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {opt.name}
                {opt.price > 0 && (
                  <span className="text-xs text-gray-500 ml-1">
                    +{formatCurrency(opt.price)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Temperature Options */}
      {showIceOptions && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Đá</label>
          <div className="flex flex-wrap gap-2">
            {TEMPERATURE_OPTIONS.map((temp) => (
              <button
                key={temp}
                onClick={() => setOptions({ ...options, temperature: temp })}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  options.temperature === temp
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {temp}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sweetness Options */}
      {showSweetnessOptions && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Độ ngọt</label>
          <div className="flex flex-wrap gap-2">
            {SWEETNESS_OPTIONS.map((sweet) => (
              <button
                key={sweet}
                onClick={() => setOptions({ ...options, sweetness: sweet })}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  options.sweetness === sweet
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {sweet}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toppings */}
      {showToppingOptions && availableToppings.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Topping</label>
          <div className="flex flex-wrap gap-2">
            {availableToppings.map((topping) => {
              const isSelected = options.selectedToppings.some(t => t._id === topping._id);
              return (
                <button
                  key={topping._id}
                  onClick={() => toggleTopping(topping)}
                  className={`px-3 py-1.5 rounded-lg border text-sm flex items-center ${
                    isSelected
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {isSelected && <FiCheck size={14} className="mr-1" />}
                  {topping.name}
                  <span className="text-xs text-gray-500 ml-1">
                    +{formatCurrency(topping.price)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Quantity */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Số lượng</label>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setOptions({ ...options, quantity: Math.max(1, options.quantity - 1) })}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
          >
            <FiMinus size={18} />
          </button>
          <input
            type="number"
            min="1"
            value={options.quantity}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 1;
              setOptions({ ...options, quantity: Math.max(1, val) });
            }}
            className="w-16 h-10 text-center text-xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            onClick={() => setOptions({ ...options, quantity: options.quantity + 1 })}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
          >
            <FiPlus size={18} />
          </button>
        </div>
      </div>

      {/* Total & Add Button */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          <span className="text-gray-600">Tổng: </span>
          <span className="text-xl font-bold text-green-600">{formatCurrency(totalPrice)}</span>
        </div>
        <button
          onClick={onAdd}
          className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
        >
          Thêm vào đơn
        </button>
      </div>
    </div>
  );
};

export default ItemConfigPanel;
